import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const DateTimeInput = React.forwardRef(({
  className,
  label,
  id,
  error,
  required = false,
  disableAutofill = false,
  value,
  onChange,
  placeholder = "Select date and time",
  minDate,
  maxDate,
  minTime,
  defaultToCurrentTime = true,
  onOpenChange,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState(value ? new Date(value) : null);
  const [displayValue, setDisplayValue] = useState(value || '');
  const [position, setPosition] = useState('bottom');
  const datePickerRef = useRef(null);
  const inputRef = useRef(null);


  const now = new Date();
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentTimeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const parseValueString = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val?.target?.value && typeof val.target.value === 'string') return val.target.value;
    return '';
  };

  const [time, setTime] = useState(() => {
    const valStr = parseValueString(value);
    if (valStr && valStr.includes('T')) {
      return valStr.split('T')[1]?.substring(0, 5) || (defaultToCurrentTime ? currentTimeString : '00:00');
    }
    return defaultToCurrentTime ? currentTimeString : '00:00';
  });

  useEffect(() => {
    const valStr = parseValueString(value);
    if (valStr) {
      const dateObj = new Date(valStr);
      const timePart = valStr.includes('T') ? valStr.split('T')[1]?.substring(0, 5) : (defaultToCurrentTime ? currentTimeString : '00:00');
      setDate(isNaN(dateObj.getTime()) ? null : dateObj);
      setTime(timePart || (defaultToCurrentTime ? currentTimeString : '00:00'));
      setDisplayValue(isNaN(dateObj.getTime()) ? '' : formatDateTime(dateObj, timePart || (defaultToCurrentTime ? currentTimeString : '00:00')));
    } else {
      setDate(null);
      setTime(defaultToCurrentTime ? currentTimeString : '00:00');
      setDisplayValue('');
    }
  }, [value, defaultToCurrentTime]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen && inputRef.current) {
        updatePosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const updatePosition = () => {
    if (inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      const pickerHeight = 300;

      if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {

        if (inputRect.top > pickerHeight) {
          setPosition('top');
        } else {

          setPosition('bottom');
        }
      } else {
        setPosition('bottom');
      }
    }
  };

  const formatDateTime = (dateObj, timeStr) => {
    if (!dateObj) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day} ${timeStr}`;
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);

    const timeStr = time || (defaultToCurrentTime ? currentTimeString : '00:00');
    const formatted = formatDateTime(newDate, timeStr);
    setDisplayValue(formatted);

    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const day = String(newDate.getDate()).padStart(2, '0');
    const isoString = `${year}-${month}-${day}T${timeStr}`;

    if (onChange) {
      const event = {
        target: {
          value: isoString,
          id
        }
      };
      onChange(event);
    }
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setTime(newTime);

    if (date) {
      const formatted = formatDateTime(date, newTime);
      setDisplayValue(formatted);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const isoString = `${year}-${month}-${day}T${newTime}`;

      if (onChange) {
        const event = {
          target: {
            value: isoString,
            id
          }
        };
        onChange(event);
      }
    }
  };

  const togglePicker = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };


  const getCalendarDays = () => {
    const currentDate = date || new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];


    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, prevMonthLastDay - i);

      const isBeforeMinDate = minDate && dayDate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());

      const isAfterMaxDate = maxDate && dayDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      const isDisabled = isBeforeMinDate || isAfterMaxDate;
      days.push({
        date: dayDate,
        isCurrentMonth: false,
        isDisabled: isDisabled
      });
    }


    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);

      const isBeforeMinDate = minDate && dayDate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());

      const isAfterMaxDate = maxDate && dayDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      const isDisabled = isBeforeMinDate || isAfterMaxDate;
      days.push({
        date: dayDate,
        isCurrentMonth: true,
        isDisabled: isDisabled
      });
    }


    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const dayDate = new Date(year, month + 1, i);

      const isBeforeMinDate = minDate && dayDate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());

      const isAfterMaxDate = maxDate && dayDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      const isDisabled = isBeforeMinDate || isAfterMaxDate;
      days.push({
        date: dayDate,
        isCurrentMonth: false,
        isDisabled: isDisabled
      });
    }

    return days;
  };

  const selectDate = (selectedDate) => {

    const isBeforeMinDate = minDate && selectedDate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const isAfterMaxDate = maxDate && selectedDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    if (isBeforeMinDate || isAfterMaxDate) return;

    handleDateChange(selectedDate);
    setIsOpen(false);
  };

  const changeMonth = (direction) => {
    const newDate = date ? new Date(date) : new Date();
    newDate.setMonth(newDate.getMonth() + direction);
    setDate(newDate);
  };


  const isTimeDisabled = (timeValue) => {

    if (minTime && date) {
      const today = new Date();
      const selectedDateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const currentDateWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());


      if (selectedDateWithoutTime.getTime() === currentDateWithoutTime.getTime()) {
        return timeValue < minTime;
      }
    }
    return false;
  };

  return (
    <div className="w-full" ref={datePickerRef}>
      {label && (
        <Label
          htmlFor={id}
          className={`text-sm font-medium mb-1 inline-block ${error ? 'text-destructive' : 'text-foreground'}`}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            id={id}
            type="text"
            value={date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : ''}
            readOnly
            placeholder={placeholder}
            className={`
              flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm 
              ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium 
              placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 
              focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed 
              disabled:opacity-50 transition-all duration-200
              pl-3 pr-10 cursor-pointer
              ${className || ''} 
              ${error ? 'border-destructive focus-visible:ring-destructive/30' : 'border-input focus-visible:ring-primary/30 hover:border-foreground/30'} 
            `}
            onClick={togglePicker}
            autoComplete={disableAutofill ? 'off' : props.autoComplete}
            {...props}
          />
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
            onClick={togglePicker}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>
        <div className="relative w-32 shrink-0">
          <input
            type="time"
            value={time}
            onChange={handleTimeChange}
            disabled={props.disabled}
            onClick={(e) => {
              try {
                if (e.target.showPicker) e.target.showPicker();
              } catch (err) {}
            }}
            min={isTimeDisabled('00:00') ? minTime : undefined}
            className={`
              flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm 
              ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium 
              placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 
              focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed 
              disabled:opacity-50 transition-all duration-200
              ${error ? 'border-destructive focus-visible:ring-destructive/30' : 'border-input focus-visible:ring-primary/30 hover:border-foreground/30'} 
            `}
          />
        </div>

        {isOpen && (
          <div
            className={`absolute z-[9999] w-full bg-background border border-input rounded-md shadow-lg ${position === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
            style={{ maxHeight: '300px', overflow: 'hidden' }}
          >
            { }
            <div className="flex items-center justify-between p-2 border-b">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => changeMonth(-1)}
                className="h-8 w-8 p-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
              <div className="font-medium">
                {date ? date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => changeMonth(1)}
                className="h-8 w-8 p-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 p-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {day}
                </div>
              ))}
              {getCalendarDays().map((dayObj, index) => {
                const isSelected = date && dayObj.date.toDateString() === date.toDateString();
                return (
                  <Button
                    key={index}
                    variant={isSelected ? "default" : "ghost"}
                    size="sm"
                    type="button"
                    disabled={dayObj.isDisabled}
                    className={`
                      h-8 w-8 p-0 text-xs
                      ${!dayObj.isCurrentMonth ? 'text-muted-foreground' : ''}
                      ${isSelected ? 'bg-primary text-primary-foreground' : ''}
                      ${dayObj.isDisabled ? 'text-muted-foreground/50 cursor-not-allowed' : ''}
                    `}
                    onClick={() => !dayObj.isDisabled && selectDate(dayObj.date)}
                  >
                    {dayObj.date.getDate()}
                  </Button>
                );
              })}
            </div>

          </div>
        )}
      </div>
      {error && (
        <p className="text-[11px] text-destructive mt-0.5 animate-in fade-in slide-in-from-top-1 transition-all duration-200">
          {error}
        </p>
      )}
    </div>
  );
});

DateTimeInput.displayName = 'DateTimeInput';

export { DateTimeInput };